import React from 'react';
import SEO from '../components/SEO';
import { PageHero } from '../components/marketing';

const SECTIONS = [
  {
    title: '1. About These Terms',
    body: (
      <p>
        These Terms of Service ("Terms") govern your access to and use of the True Global Education website
        (<strong>trueglobaleducation.com</strong>), our student and recruiter portals, and the consulting, application,
        admission, financial-aid, visa and recruiter-affiliate services we provide (collectively, the "Services"). By
        accessing the site, submitting an inquiry or creating an account you agree to be bound by these Terms and by
        our Privacy Policy.
      </p>
    ),
  },
  {
    title: '2. Who We Are',
    body: (
      <p>
        "True Global", "we", "us" and "our" refer to True Global Education, registered in British Columbia, Canada,
        with offices at 8166 128 St #109, Surrey, BC V3W 4G1. You can reach us at{' '}
        <a href="mailto:info@trueglobaleducation.com" className="text-tge-navy underline">info@trueglobaleducation.com</a>{' '}
        or <a href="tel:6048141515" className="text-tge-navy underline">+1 604 814 1515</a>.
      </p>
    ),
  },
  {
    title: '3. Eligibility',
    body: (
      <p>
        You must be at least 16 years old to use our Services. If you are between 16 and 18 a parent or legal guardian
        must co-sign your engagement agreement. By using the Services you represent that you have the legal capacity
        to enter into a binding agreement in your jurisdiction.
      </p>
    ),
  },
  {
    title: '4. Our Services',
    body: (
      <>
        <p>
          We provide career counselling, test-preparation guidance, college and university admissions support,
          student-aid and scholarship advisory, and study-permit/visa documentation support. The precise scope, fees,
          timelines and refund policy applicable to a paid engagement are set out in a separate written service
          agreement that you sign before we begin work.
        </p>
        <p>
          We do not guarantee any specific admission, scholarship, visa or immigration outcome. Decisions are made
          solely by the receiving institution or government authority based on their own criteria.
        </p>
      </>
    ),
  },
  {
    title: '5. Free Consultation & Lead Forms',
    body: (
      <p>
        Submitting a contact form, downloading a guide, or filling out an ad lead form (including Meta, Google or
        TikTok lead ads) does not by itself create a client relationship. It only authorises our team to contact you
        by phone, SMS, WhatsApp, email or AI-assisted voice call to assess fit. A formal engagement begins only when
        a signed service agreement and applicable fees are in place.
      </p>
    ),
  },
  {
    title: '6. Recruiter Portal',
    body: (
      <p>
        Independent education recruiters who register on our Recruiter Portal agree to the separate Recruiter
        Agreement signed via DocuSign, including commission terms, code of conduct, anti-poaching obligations and
        compliance with applicable advertising and immigration laws in their territory. Misrepresentation of True
        Global, fraudulent applications, or unauthorised use of student data will result in immediate termination and
        forfeiture of pending commissions.
      </p>
    ),
  },
  {
    title: '7. Fees & Payments',
    body: (
      <>
        <p>
          Service fees are quoted in Canadian dollars (CAD) unless stated otherwise and are payable as set out in your
          signed engagement agreement. Third-party fees (institution application fees, test fees, visa fees,
          credential evaluation fees, courier fees, translations, etc.) are your responsibility and are not included
          in our service fees unless explicitly stated.
        </p>
        <p>
          Refunds, if any, are governed by your signed engagement agreement. Once an application has been submitted
          to an institution or a visa authority, service fees attributable to that work are non-refundable.
        </p>
      </>
    ),
  },
  {
    title: '8. Your Responsibilities',
    body: (
      <>
        <p>You agree to:</p>
        <ul>
          <li>provide accurate, complete and up-to-date information;</li>
          <li>upload only authentic documents that belong to you;</li>
          <li>respond to our reasonable requests for information within the timelines required by each institution or
          visa authority;</li>
          <li>not impersonate any person, recruiter or institution;</li>
          <li>not misuse, copy, scrape, reverse-engineer or interfere with the Services; and</li>
          <li>comply with all applicable laws, including immigration, anti-fraud and advertising laws of your country
          and the destination country.</li>
        </ul>
        <p>
          You are solely responsible for any consequence (including refusal of admission, scholarship clawback, visa
          refusal or deportation) caused by inaccurate, falsified or incomplete information you supply.
        </p>
      </>
    ),
  },
  {
    title: '9. Intellectual Property',
    body: (
      <p>
        All content on the site — including text, design, logos, brochures, photographs, downloadable templates,
        software and the underlying platform — is owned by True Global Education or its licensors and is protected
        by copyright, trademark and other laws. You may not copy, reproduce, redistribute or create derivative works
        without our prior written consent. Personal, non-commercial viewing of the site is permitted.
      </p>
    ),
  },
  {
    title: '10. AI & Automated Communications',
    body: (
      <p>
        Our platform uses AI tools (including large-language models from OpenAI, Anthropic and Google) for lead
        scoring, document review, translation, voice-call drafting and chatbot responses. AI-generated suggestions
        are reviewed by a human counsellor before any decision is made on your file. We also use Twilio and similar
        providers to send automated SMS, WhatsApp and pre-recorded or AI-generated voice messages. By giving us your
        phone number you consent to receive such communications. You can opt out anytime by replying STOP, by
        emailing us, or by adjusting preferences in your portal.
      </p>
    ),
  },
  {
    title: '11. Third-Party Sites & Services',
    body: (
      <p>
        We may link to third-party websites (colleges, scholarship providers, payment processors, Meta, Google,
        DocuSign, etc.). Your use of those sites is governed by their own terms and privacy policies. We are not
        responsible for content, products or services offered by third parties.
      </p>
    ),
  },
  {
    title: '12. Disclaimers',
    body: (
      <p>
        The Services are provided "as is" and "as available". To the maximum extent permitted by law we disclaim all
        warranties of any kind, whether express or implied, including merchantability, fitness for a particular
        purpose and non-infringement. We do not warrant that the Services will be uninterrupted, error-free or that
        any content is accurate at all times.
      </p>
    ),
  },
  {
    title: '13. Limitation of Liability',
    body: (
      <p>
        To the maximum extent permitted by law, True Global Education and its directors, employees, contractors and
        recruiter partners will not be liable for any indirect, incidental, special, consequential or punitive
        damages, or any loss of profits, revenues, data, business opportunity or goodwill, arising out of or in
        connection with your use of the Services. Our total aggregate liability for any direct damages will not
        exceed the total amount of fees you have paid us in the 12 months immediately preceding the event giving rise
        to the claim.
      </p>
    ),
  },
  {
    title: '14. Indemnity',
    body: (
      <p>
        You agree to indemnify and hold harmless True Global Education and its representatives against any claims,
        damages, liabilities, costs and expenses (including reasonable legal fees) arising from your breach of these
        Terms, your misuse of the Services or your provision of false or misleading information.
      </p>
    ),
  },
  {
    title: '15. Termination',
    body: (
      <p>
        We may suspend or terminate your access to the Services at any time, with or without cause, including if we
        reasonably believe you have violated these Terms or any applicable law. Provisions that by their nature
        should survive termination (including payment obligations, intellectual property, disclaimers, limitation of
        liability and indemnity) will survive.
      </p>
    ),
  },
  {
    title: '16. Governing Law & Dispute Resolution',
    body: (
      <p>
        These Terms are governed by the laws of the Province of British Columbia and the federal laws of Canada
        applicable in BC, without regard to conflict-of-law rules. The parties submit to the exclusive jurisdiction
        of the courts located in Surrey, British Columbia, except that either party may seek injunctive relief in any
        court of competent jurisdiction to protect its intellectual property or confidential information.
      </p>
    ),
  },
  {
    title: '17. Changes to These Terms',
    body: (
      <p>
        We may revise these Terms from time to time. When we make material changes we will update the "Last updated"
        date below and, where appropriate, notify you by email or with a banner on the site. Your continued use of
        the Services after changes become effective constitutes acceptance of the revised Terms.
      </p>
    ),
  },
  {
    title: '18. Contact',
    body: (
      <p>
        Questions about these Terms? Email{' '}
        <a href="mailto:info@trueglobaleducation.com" className="text-tge-navy underline">info@trueglobaleducation.com</a>{' '}
        or call <a href="tel:6048141515" className="text-tge-navy underline">+1 604 814 1515</a>.
      </p>
    ),
  },
];

export default function TermsPage() {
  return (
    <div className="bg-tge-paper min-h-screen" data-testid="terms-page">
      <SEO
        title="Terms of Service — True Global Education"
        description="The terms that govern your use of the True Global Education website, student and recruiter portals and consulting services."
      />
      <PageHero
        eyebrow="Legal"
        title="Terms of Service"
        intro="Plain-language ground rules for using our website, portals and consulting services. By using True Global Education you agree to these terms."
      />
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
          <p className="text-sm text-tge-muted mb-10">
            <strong>Last updated:</strong> 28 May 2026
          </p>

          <div className="space-y-12">
            {SECTIONS.map((s) => (
              <article key={s.title} data-testid={`terms-section-${s.title.split('.')[0].trim()}`}>
                <h2 className="font-serif-display text-2xl sm:text-3xl text-tge-navy mb-4">
                  {s.title}
                </h2>
                <div className="text-tge-muted text-base leading-relaxed space-y-4 [&_a]:text-tge-navy [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_strong]:text-tge-navy">
                  {s.body}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
